import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../utils/http";
import { useSession } from "../context/SessionContext";
import { isSnoozed, readLocal, remember, reviewUrlFrom, storeInvites } from "../utils/googleReview";

// Whether to invite this guest to review the store on Google, and what
// happens when they answer.
//
// The store's own settings decide whether there is anything to ask at all — a
// store that hasn't pasted a review link makes no request here. Past that,
// the API's answer (GET v1/google-review) is what decides, since it remembers
// across the guest tokens this app mints on every scan; this device's own
// memory covers a store whose API doesn't have the endpoint yet.
export default function useGoogleReview({ orderId } = {}) {
  const { store, ready } = useSession();
  const storeId = store?.id;
  const configured = storeInvites(store);

  // The API's word, null while it hasn't given one.
  const [review, setReview] = useState(null);
  const [asked, setAsked] = useState(false);
  // What they answered just now, which shows the thank-you before any of the
  // above has caught up.
  const [answer, setAnswer] = useState(null);
  const [local, setLocal] = useState(() => readLocal(storeId));

  useEffect(() => {
    setLocal(readLocal(storeId));
  }, [storeId]);

  useEffect(() => {
    if (!configured || !ready) return undefined;
    let alive = true;
    apiClient
      .get("google-review")
      .then((res) => {
        if (alive) setReview(res.data?.review ?? null);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setAsked(true);
      });
    return () => {
      alive = false;
    };
  }, [configured, ready]);

  const save = useCallback(
    (action) => {
      apiClient
        .post("google-review", { action, order_id: orderId ?? null, source: "self_order" })
        // Nothing to tell the guest: this device has remembered it either
        // way, and the invite is gone from the screen.
        .catch(() => {});
    },
    [orderId]
  );

  const rate = useCallback(() => {
    const url = review?.url || reviewUrlFrom(store?.settings?.google_review_url);
    // Opened from the tap itself, before anything is awaited, so the browser
    // doesn't take Google's page for a popup and block it.
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setAnswer("reviewed");
    setLocal(remember(storeId, { reviewed: true }));
    save("reviewed");
  }, [review, store, storeId, save]);

  const later = useCallback(() => {
    setAnswer("later");
    setLocal(remember(storeId, { laterAt: Date.now() }));
    save("later");
  }, [storeId, save]);

  const reviewed = answer === "reviewed" || !!review?.reviewed || !!local.reviewed;
  const waiting = configured && ready && !asked;
  const show =
    configured &&
    !waiting &&
    !reviewed &&
    answer !== "later" &&
    !isSnoozed(local) &&
    // The API remembers this guest across sessions; without its answer this
    // device's memory is all there is to go on.
    (review ? !!review.prompt : true);

  return { show, thanked: answer === "reviewed", rate, later };
}
